/**
 * Générateur XML EN 16931 (Cross Industry Invoice) pour Factur-X
 * Conforme à la norme européenne de facturation électronique
 * Version: 2.0.0
 *
 * ⚠️ DEPRECATION NOTICE (Phase 3 - Sprint Financial Architecture)
 * @see docs/SPRINT_FINANCIAL_ARCHITECTURE.md
 *
 * Ce module frontend est deprecated. La génération XML Factur-X doit être
 * effectuée côté backend via:
 * - backend/src/modules/compliance/services/facturx-generator.service.ts
 * - backend/src/modules/compliance/services/en16931-xml-generator.service.ts
 *
 * Fonctions deprecated mais conservées (utilisées par pdfGenerator.ts):
 * - calculateTaxTotals() → Backend calcule les totaux dans le service
 * - calculateTotals() → Backend calcule les totaux dans le service
 * - generateFacturXML() → Utiliser backend FacturXGeneratorService
 *
 * Fonctions CONSERVÉES (formatting only - pas de calcul):
 * - formatAmount(), formatDate() - formatting
 * - escapeXml() - XML sanitization
 * - normalizeVATNumber() - VAT formatting
 */

import { format } from 'date-fns';

export interface FacturXItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  taxRate: number; // Taux de TVA en pourcentage (5.5, 10, 20)
  total: number;
}

export interface FacturXData {
  // Numérotation
  invoiceNumber: string;
  invoiceDate: Date;
  dueDate?: Date;
  buyerReference?: string; // Référence acheteur (BT-10) - requis pour XRechnung

  // Société (Vendeur)
  seller: {
    name: string;
    address: string;
    postalCode: string;
    city: string;
    country: string;
    siret: string;
    vatNumber: string; // TVA intracommunautaire
    email?: string;
    phone?: string;
    iban?: string;
    bic?: string;
  };

  // Client (Acheteur)
  buyer: {
    name: string;
    address: string;
    postalCode: string;
    city: string;
    country: string;
    email?: string;
    phone?: string;
    vatNumber?: string;
  };

  // Lignes de facturation
  items: FacturXItem[];

  // Mentions légales et conditions
  paymentTerms?: string;
  legalMentions?: string;
  notes?: string;

  // Remise éventuelle
  discountAmount?: number;
  discountReason?: string;
}

/**
 * Calcule les totaux par taux de TVA avec répartition proportionnelle de la remise
 * @deprecated Use backend FacturXGeneratorService - totals calculated server-side
 */
function calculateTaxTotals(items: FacturXItem[], discountAmount: number = 0) {
  const taxGroups: {
    [rate: number]: {
      base: number;
      amount: number;
      discountPortion: number; // Part de remise pour ce taux
    }
  } = {};

  // Grouper par taux de TVA
  items.forEach(item => {
    if (!taxGroups[item.taxRate]) {
      taxGroups[item.taxRate] = { base: 0, amount: 0, discountPortion: 0 };
    }
    taxGroups[item.taxRate].base += item.total;
  });

  // Appliquer la remise proportionnellement et enregistrer chaque portion
  if (discountAmount > 0) {
    const totalBeforeDiscount = Object.values(taxGroups).reduce((sum, g) => sum + g.base, 0);
    Object.keys(taxGroups).forEach(rate => {
      const rateNum = parseFloat(rate);
      const proportion = taxGroups[rateNum].base / totalBeforeDiscount;
      const discountForThisRate = discountAmount * proportion;

      taxGroups[rateNum].discountPortion = discountForThisRate;
      taxGroups[rateNum].base -= discountForThisRate;
    });
  }

  // Calculer les montants de TVA
  Object.keys(taxGroups).forEach(rate => {
    const rateNum = parseFloat(rate);
    taxGroups[rateNum].amount = (taxGroups[rateNum].base * rateNum) / 100;
  });

  return taxGroups;
}

/**
 * Calcule les totaux globaux
 * @deprecated Use backend FacturXGeneratorService - totals calculated server-side
 */
function calculateTotals(items: FacturXItem[], discountAmount: number = 0) {
  const subtotal = items.reduce((sum, item) => sum + item.total, 0);
  const subtotalAfterDiscount = subtotal - (discountAmount || 0);

  const taxGroups = calculateTaxTotals(items, discountAmount);
  const totalTax = Object.values(taxGroups).reduce((sum, g) => sum + g.amount, 0);
  const total = subtotalAfterDiscount + totalTax;

  return {
    subtotal,
    subtotalAfterDiscount,
    totalTax,
    total,
    taxGroups
  };
}

/**
 * Formate un montant pour le XML (2 décimales)
 */
function formatAmount(amount: number): string {
  return amount.toFixed(2);
}

/**
 * Formate une date au format ISO (YYYY-MM-DD)
 */
function formatDate(date: Date): string {
  return format(date, 'yyyyMMdd');
}

/**
 * Échappe les caractères spéciaux XML pour éviter la corruption
 */
function escapeXml(text: string): string {
  if (!text) return '';
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/**
 * Normalise le numéro de TVA avec le préfixe pays ISO 3166-1 alpha-2
 * Exemples: "FR87523431328" ou "87523431328" → "FR87523431328"
 */
function normalizeVATNumber(vatNumber: string, countryCode: string): string {
  if (!vatNumber) return '';

  // Retirer tous les espaces et caractères spéciaux
  const cleaned = vatNumber.replace(/[\s\.\-]/g, '').toUpperCase();

  // Si le numéro commence déjà par un code pays valide (2 lettres), le retourner tel quel
  if (/^[A-Z]{2}/.test(cleaned)) {
    return cleaned;
  }

  // Sinon, ajouter le code pays au début
  return `${countryCode.toUpperCase()}${cleaned}`;
}

/**
 * Génère le XML Cross Industry Invoice (CII) conforme EN 16931
 * @deprecated Use backend API GET /api/v1/invoices/:id/facturx instead
 * The backend FacturXGeneratorService handles all XML generation server-side
 */
export function generateFacturXML(data: FacturXData): string {
  const totals = calculateTotals(data.items, data.discountAmount);
  const invoiceDate = formatDate(data.invoiceDate);
  const dueDate = data.dueDate ? formatDate(data.dueDate) : invoiceDate;

  // Normaliser les numéros de TVA avec préfixe pays
  const sellerVAT = normalizeVATNumber(data.seller.vatNumber, data.seller.country);
  const buyerVAT = data.buyer.vatNumber ? normalizeVATNumber(data.buyer.vatNumber, data.buyer.country) : '';

  // Construction du XML EN 16931 (CII - Cross Industry Invoice)
  const xml = `<?xml version="1.0" encoding="UTF-8"?>
<rsm:CrossIndustryInvoice xmlns:rsm="urn:un:unece:uncefact:data:standard:CrossIndustryInvoice:100"
                          xmlns:qdt="urn:un:unece:uncefact:data:standard:QualifiedDataType:100"
                          xmlns:ram="urn:un:unece:uncefact:data:standard:ReusableAggregateBusinessInformationEntity:100"
                          xmlns:xs="http://www.w3.org/2001/XMLSchema"
                          xmlns:udt="urn:un:unece:uncefact:data:standard:UnqualifiedDataType:100">

  <!-- Contexte du document -->
  <rsm:ExchangedDocumentContext>
    <ram:BusinessProcessSpecifiedDocumentContextParameter>
      <ram:ID>A1</ram:ID>
    </ram:BusinessProcessSpecifiedDocumentContextParameter>
    <ram:GuidelineSpecifiedDocumentContextParameter>
      <ram:ID>urn:cen.eu:en16931:2017</ram:ID>
    </ram:GuidelineSpecifiedDocumentContextParameter>
  </rsm:ExchangedDocumentContext>

  <!-- En-tête du document -->
  <rsm:ExchangedDocument>
    <ram:ID>${escapeXml(data.invoiceNumber)}</ram:ID>
    <ram:TypeCode>380</ram:TypeCode> <!-- 380 = Facture commerciale -->
    <ram:IssueDateTime>
      <udt:DateTimeString format="102">${invoiceDate}</udt:DateTimeString>
    </ram:IssueDateTime>
${data.notes ? `    <ram:IncludedNote>
      <ram:Content>${escapeXml(data.notes)}</ram:Content>
    </ram:IncludedNote>` : ''}
  </rsm:ExchangedDocument>

  <!-- Transaction commerciale -->
  <rsm:SupplyChainTradeTransaction>

    <!-- Lignes de facturation (DOIVENT être en premier selon EN 16931) -->
${data.items.map((item, index) => `    <ram:IncludedSupplyChainTradeLineItem>
      <ram:AssociatedDocumentLineDocument>
        <ram:LineID>${index + 1}</ram:LineID>
      </ram:AssociatedDocumentLineDocument>
      <ram:SpecifiedTradeProduct>
        <ram:Name>${escapeXml(item.description)}</ram:Name>
      </ram:SpecifiedTradeProduct>
      <ram:SpecifiedLineTradeAgreement>
        <ram:NetPriceProductTradePrice>
          <ram:ChargeAmount>${formatAmount(item.unitPrice)}</ram:ChargeAmount>
        </ram:NetPriceProductTradePrice>
      </ram:SpecifiedLineTradeAgreement>
      <ram:SpecifiedLineTradeDelivery>
        <ram:BilledQuantity unitCode="C62">${item.quantity}</ram:BilledQuantity>
      </ram:SpecifiedLineTradeDelivery>
      <ram:SpecifiedLineTradeSettlement>
        <ram:ApplicableTradeTax>
          <ram:TypeCode>VAT</ram:TypeCode>
          <ram:CategoryCode>S</ram:CategoryCode>
          <ram:RateApplicablePercent>${item.taxRate}</ram:RateApplicablePercent>
        </ram:ApplicableTradeTax>
        <ram:SpecifiedTradeSettlementLineMonetarySummation>
          <ram:LineTotalAmount>${formatAmount(item.total)}</ram:LineTotalAmount>
        </ram:SpecifiedTradeSettlementLineMonetarySummation>
      </ram:SpecifiedLineTradeSettlement>
    </ram:IncludedSupplyChainTradeLineItem>`).join('\n')}

    <!-- Informations sur les parties -->
    <ram:ApplicableHeaderTradeAgreement>
${data.buyerReference ? `      <ram:BuyerReference>${escapeXml(data.buyerReference)}</ram:BuyerReference>` : `      <ram:BuyerReference>${escapeXml(data.invoiceNumber)}</ram:BuyerReference>`}

      <!-- Vendeur (Société) -->
      <ram:SellerTradeParty>
        <ram:Name>${escapeXml(data.seller.name)}</ram:Name>
        <ram:SpecifiedLegalOrganization>
          <ram:ID>${data.seller.siret.replace(/\s/g, '')}</ram:ID>
        </ram:SpecifiedLegalOrganization>
        <ram:DefinedTradeContact>
          <ram:PersonName>${escapeXml(data.seller.name)}</ram:PersonName>
${data.seller.phone ? `          <ram:TelephoneUniversalCommunication>
            <ram:CompleteNumber>${escapeXml(data.seller.phone)}</ram:CompleteNumber>
          </ram:TelephoneUniversalCommunication>` : ''}
${data.seller.email ? `          <ram:EmailURIUniversalCommunication>
            <ram:URIID>${escapeXml(data.seller.email)}</ram:URIID>
          </ram:EmailURIUniversalCommunication>` : ''}
        </ram:DefinedTradeContact>
        <ram:PostalTradeAddress>
          <ram:PostcodeCode>${escapeXml(data.seller.postalCode)}</ram:PostcodeCode>
          <ram:LineOne>${escapeXml(data.seller.address)}</ram:LineOne>
          <ram:CityName>${escapeXml(data.seller.city)}</ram:CityName>
          <ram:CountryID>${data.seller.country}</ram:CountryID>
        </ram:PostalTradeAddress>
${data.seller.email ? `        <ram:URIUniversalCommunication>
          <ram:URIID schemeID="EM">${escapeXml(data.seller.email)}</ram:URIID>
        </ram:URIUniversalCommunication>` : ''}
        <ram:SpecifiedTaxRegistration>
          <ram:ID schemeID="VA">${sellerVAT}</ram:ID>
        </ram:SpecifiedTaxRegistration>
      </ram:SellerTradeParty>

      <!-- Acheteur (Client) -->
      <ram:BuyerTradeParty>
        <ram:Name>${escapeXml(data.buyer.name)}</ram:Name>
        <ram:PostalTradeAddress>
          <ram:PostcodeCode>${escapeXml(data.buyer.postalCode)}</ram:PostcodeCode>
          <ram:LineOne>${escapeXml(data.buyer.address)}</ram:LineOne>
          <ram:CityName>${escapeXml(data.buyer.city)}</ram:CityName>
          <ram:CountryID>${data.buyer.country}</ram:CountryID>
        </ram:PostalTradeAddress>
${data.buyer.email ? `        <ram:URIUniversalCommunication>
          <ram:URIID schemeID="EM">${escapeXml(data.buyer.email)}</ram:URIID>
        </ram:URIUniversalCommunication>` : ''}
${buyerVAT ? `        <ram:SpecifiedTaxRegistration>
          <ram:ID schemeID="VA">${buyerVAT}</ram:ID>
        </ram:SpecifiedTaxRegistration>` : ''}
      </ram:BuyerTradeParty>

    </ram:ApplicableHeaderTradeAgreement>

    <!-- Informations de livraison -->
    <ram:ApplicableHeaderTradeDelivery>
      <ram:ActualDeliverySupplyChainEvent>
        <ram:OccurrenceDateTime>
          <udt:DateTimeString format="102">${invoiceDate}</udt:DateTimeString>
        </ram:OccurrenceDateTime>
      </ram:ActualDeliverySupplyChainEvent>
    </ram:ApplicableHeaderTradeDelivery>

    <!-- Détails de règlement -->
    <ram:ApplicableHeaderTradeSettlement>
      <ram:InvoiceCurrencyCode>EUR</ram:InvoiceCurrencyCode>

${data.seller.iban ? `      <!-- Coordonnées bancaires -->
      <ram:SpecifiedTradeSettlementPaymentMeans>
        <ram:TypeCode>58</ram:TypeCode> <!-- 58 = Virement SEPA -->
        <ram:PayeePartyCreditorFinancialAccount>
          <ram:IBANID>${data.seller.iban}</ram:IBANID>
        </ram:PayeePartyCreditorFinancialAccount>
${data.seller.bic ? `        <ram:PayeeSpecifiedCreditorFinancialInstitution>
          <ram:BICID>${data.seller.bic}</ram:BICID>
        </ram:PayeeSpecifiedCreditorFinancialInstitution>` : ''}
      </ram:SpecifiedTradeSettlementPaymentMeans>` : ''}

      <!-- Détails de la TVA par taux -->
${Object.entries(totals.taxGroups).map(([rate, amounts]) => `      <ram:ApplicableTradeTax>
        <ram:CalculatedAmount>${formatAmount(amounts.amount)}</ram:CalculatedAmount>
        <ram:TypeCode>VAT</ram:TypeCode>
        <ram:BasisAmount>${formatAmount(amounts.base)}</ram:BasisAmount>
        <ram:CategoryCode>S</ram:CategoryCode>
        <ram:RateApplicablePercent>${rate}</ram:RateApplicablePercent>
      </ram:ApplicableTradeTax>`).join('\n')}

${data.discountAmount && data.discountAmount > 0 ? Object.entries(totals.taxGroups)
        .filter(([_, amounts]) => amounts.discountPortion > 0)
        .map(([rate, amounts]) => `      <!-- Remise proportionnelle pour TVA ${rate}% -->
      <ram:SpecifiedTradeAllowanceCharge>
        <ram:ChargeIndicator>
          <udt:Indicator>false</udt:Indicator>
        </ram:ChargeIndicator>
        <ram:ActualAmount>${formatAmount(amounts.discountPortion)}</ram:ActualAmount>
${data.discountReason ? `        <ram:Reason>${escapeXml(data.discountReason)}</ram:Reason>` : ''}
        <ram:CategoryTradeTax>
          <ram:TypeCode>VAT</ram:TypeCode>
          <ram:CategoryCode>S</ram:CategoryCode>
          <ram:RateApplicablePercent>${rate}</ram:RateApplicablePercent>
        </ram:CategoryTradeTax>
      </ram:SpecifiedTradeAllowanceCharge>`).join('\n') : ''}

${data.paymentTerms ? `      <!-- Conditions de paiement -->
      <ram:SpecifiedTradePaymentTerms>
        <ram:Description>${escapeXml(data.paymentTerms)}</ram:Description>
        <ram:DueDateDateTime>
          <udt:DateTimeString format="102">${dueDate}</udt:DateTimeString>
        </ram:DueDateDateTime>
      </ram:SpecifiedTradePaymentTerms>` : ''}

      <!-- Totaux de la facture -->
      <ram:SpecifiedTradeSettlementHeaderMonetarySummation>
        <ram:LineTotalAmount>${formatAmount(totals.subtotal)}</ram:LineTotalAmount>
        <ram:ChargeTotalAmount>0.00</ram:ChargeTotalAmount>
        <ram:AllowanceTotalAmount>${formatAmount(data.discountAmount || 0)}</ram:AllowanceTotalAmount>
        <ram:TaxBasisTotalAmount>${formatAmount(totals.subtotalAfterDiscount)}</ram:TaxBasisTotalAmount>
        <ram:TaxTotalAmount currencyID="EUR">${formatAmount(totals.totalTax)}</ram:TaxTotalAmount>
        <ram:GrandTotalAmount>${formatAmount(totals.total)}</ram:GrandTotalAmount>
        <ram:DuePayableAmount>${formatAmount(totals.total)}</ram:DuePayableAmount>
      </ram:SpecifiedTradeSettlementHeaderMonetarySummation>

    </ram:ApplicableHeaderTradeSettlement>

  </rsm:SupplyChainTradeTransaction>

</rsm:CrossIndustryInvoice>`;

  return xml;
}

